/**
 * Static copy + tokens from the internal pricing-preview upgrade modal (upgrade-modal.html).
 * CSS reference: Inter; purple #5745FA; green checks #10B981; featured card gets purple checks.
 * Featured column = `trigger.highlight` in the preview; we map app `feature` keys to the same tier.
 */

import { FEATURE_KEY_TO_LABEL } from '@/v3-dashboard/pages/Billing/tierFeatures';

/** @typedef {{ t: string, h?: boolean }} RichSeg */
/** @typedef {string | RichSeg[]} FeatureLine */

/** Same fuzzy match as FeaturesList — align gate labels with reference line copy */
const stripNumericPrefix = (s) => s.replace(/^[\d,.\s]+[A-Z]*\s*/i, '').toLowerCase();

const labelsMatch = (a, b) => {
  if (!a || !b) return false;
  const al = a.toLowerCase().trim();
  const bl = b.toLowerCase().trim();
  if (al === bl) return true;
  if (stripNumericPrefix(a) === stripNumericPrefix(b)) return true;
  // Gate labels are often a short phrase from the same bullet (e.g. "2 AI agents" vs the full agents+credits line)
  const shorter = al.length <= bl.length ? al : bl;
  const longer = al.length >= bl.length ? al : bl;
  if (shorter.length >= 8 && longer.includes(shorter)) return true;
  return false;
};

/** @param {FeatureLine} line */
export function featureLineToPlainString(line) {
  if (typeof line === 'string') return line;
  return line.map((s) => s.t).join('');
}

/**
 * When FEATURE_KEY_TO_LABEL wording differs from UPGRADE_MODAL_REFERENCE_FEATURES, match by substring.
 * @type {Record<string, string[]>}
 */
const UPGRADE_MODAL_MATCH_HINTS = {
  /** Modal bullet uses "credits/mo"; FEATURE_KEY_TO_LABEL uses "credits/month" */
  credits: ['credits'],
  chatbots_limit: ['ai agents'],
  number_of_knowledgebases: ['ai agents'],
  advanced_chatbot_branding: ['styling', 'branding'],
  show_microphone: ['voice input'],
  share_conversations: ['conversation records'],
  zapier_integration: ['zapier'],
  google_drive_integration: ['zapier', 'drive'],
  max_characters: ['knowledge', 'character'],
  data_collection: ['lead gen', 'whatsapp'],
  whatsapp_integration: ['whatsapp', 'lead gen'],
  calendly_integration: ['calendly'],
  live_agent: ['handover'],
  custom_css: ['custom css', 'domains'],
  custom_url: ['domain'],
  api_access: ['api access'],
  user_openai_api_key: ['openai'],
  team_management: ['team seats'],
  add_custom_roles: ['team seats'],
  realtime_voice_access: ['voice agents', 'whatsapp'],
  nr_ai_voice_agents: ['voice agents'],
  unanswered_question_recognition: ['unanswered'],
  rate_limiting: ['rate'],
  auto_refresh_training: ['refresh', 'knowledge'],
  voice_to_text: ['voice input'],
};

/** @type {Record<'starter'|'advanced'|'pro', FeatureLine[]>} */
export const UPGRADE_MODAL_REFERENCE_FEATURES = {
  starter: [
    [
      { t: '2', h: true },
      { t: ' AI agents, ' },
      { t: '3,000', h: true },
      { t: ' credits/mo' },
    ],
    'Conversation records',
    'Analytics dashboard',
    'Full styling & branding',
    'Voice input',
    'Zapier (7,000+ apps)',
  ],
  advanced: [
    [
      { t: '10', h: true },
      { t: ' AI agents, ' },
      { t: '12,500', h: true },
      { t: ' credits/mo' },
    ],
    'Remove Dante branding',
    'Voice agents, WhatsApp & lead gen',
    'Human handover (10 seats)',
    'Custom CSS & 5 custom domains',
    'Advanced analytics & exports',
  ],
  pro: [
    [
      { t: '50', h: true },
      { t: ' AI agents, ' },
      { t: '50,000', h: true },
      { t: ' credits/mo' },
    ],
    'API access & own OpenAI key',
    'Advanced LLM models',
    [{ t: '10', h: true }, { t: ' team seats' }],
    [{ t: '50', h: true }, { t: ' custom domains' }],
    'Dedicated Account Manager & uptime SLA',
  ],
};

export const UPGRADE_MODAL_FEATURES_INTRO = {
  starter: 'Everything in Free, plus',
  advanced: 'Everything in Starter, plus',
  pro: 'Everything in Advanced, plus',
};

/** Match --green / featured-column checks (v3 tokens) */
export const UPGRADE_MODAL_CHECK_GREEN = 'text-v3-green-primary';
export const UPGRADE_MODAL_CHECK_PURPLE = 'text-v3-purple-primary';

function getGateLabelForFeature(feature) {
  if (!feature) return null;
  if (typeof feature === 'string' && feature.startsWith('llm_model:')) {
    return FEATURE_KEY_TO_LABEL.advanced_gpt_models;
  }
  return FEATURE_KEY_TO_LABEL[feature] ?? null;
}

function lineMatchesGate(line, feature) {
  const s = featureLineToPlainString(line);
  const gateLabel = getGateLabelForFeature(feature);
  if (gateLabel && labelsMatch(s, gateLabel)) return true;
  const hints = UPGRADE_MODAL_MATCH_HINTS[feature];
  if (hints?.length) {
    const lower = s.toLowerCase();
    return hints.some((h) => lower.includes(h.toLowerCase()));
  }
  return false;
}

/**
 * Agents + credits row first; gated feature line second (when it matches a bullet after the first row).
 * If the gate matches only the first line (limits/credits), featuredRowIndex is 0.
 * @param {'starter'|'advanced'|'pro'} tierType
 * @param {string | null | undefined} feature
 * @returns {{ lines: FeatureLine[], featuredRowIndex: number | null }}
 */
export function getOrderedUpgradeModalFeatures(tierType, feature) {
  const base = UPGRADE_MODAL_REFERENCE_FEATURES[tierType];
  if (!base?.length) return { lines: [], featuredRowIndex: null };

  if (feature == null || feature === '') {
    return { lines: [...base], featuredRowIndex: null };
  }

  const agentsLine = base[0];
  const rest = base.slice(1);

  if (lineMatchesGate(agentsLine, feature)) {
    return { lines: [...base], featuredRowIndex: 0 };
  }

  const matchIdx = rest.findIndex((line) => lineMatchesGate(line, feature));
  if (matchIdx === -1) {
    return { lines: [...base], featuredRowIndex: null };
  }

  const matched = rest[matchIdx];
  const others = rest.filter((_, i) => i !== matchIdx);
  return {
    lines: [agentsLine, matched, ...others],
    featuredRowIndex: 1,
  };
}

/**
 * Which plan column is `.featured` (purple border, primary CTA, purple checkmarks).
 * Mirrors TRIGGERS[].highlight in upgrade-modal.html; unknown keys default to `advanced`.
 * @param {string | null | undefined} feature - feature gate key from openUpgradeModal
 * @returns {'starter'|'advanced'|'pro'}
 */
export function getUpgradeModalHighlightTier(feature) {
  if (feature == null || feature === '') return 'advanced';
  if (typeof feature === 'string' && feature.startsWith('llm_model:')) return 'advanced';

  /** @type {Record<string, 'starter'|'advanced'|'pro'>} */
  const map = {
    chatbots_limit: 'starter',
    number_of_knowledgebases: 'starter',
    credits: 'starter',
    max_characters: 'starter',
    share_conversations: 'starter',
    analytics_dashboard: 'starter',
    voice_to_text: 'starter',
    show_microphone: 'starter',
    show_email_details: 'starter',
    show_play_button: 'starter',
    show_welcome_message_as_tooltip: 'starter',
    zapier_integration: 'starter',
    google_drive_integration: 'starter',
    advanced_chatbot_branding: 'starter',
    remove_watermark: 'advanced',
    remove_powered_by_dante: 'advanced',
    realtime_voice_access: 'advanced',
    nr_ai_voice_agents: 'advanced',
    data_collection: 'advanced',
    whatsapp_integration: 'advanced',
    calendly_integration: 'advanced',
    live_agent: 'advanced',
    custom_css: 'advanced',
    custom_url: 'advanced',
    unanswered_question_recognition: 'advanced',
    rate_limiting: 'advanced',
    auto_refresh_training: 'advanced',
    api_access: 'pro',
    user_openai_api_key: 'pro',
    team_management: 'pro',
    add_custom_roles: 'pro',
  };

  return map[feature] ?? 'advanced';
}
